import React, { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { RouteObject } from 'react-router/lib/router';

import LoadingProgress from '@components/LoadingProgress';
import LoginPage from '@core/login/pages/LoginPage';

import EventPageContainer from './EventPageContainer';
import SelectEventPage from '@core/event/pages/SelectEventPage';
import SelectAvatarProviderPage from '@core/avatar/pages/SelectAvatarProviderPage';
import EventPage from '@core/dashboard/pages/EventPage';
import ExternalEventPage from '@core/event/pages/ExternalEventPage';
import DevicesTestPage from '@core/devices/pages/DevicesTestPage';
import PageNotFoundPage from '@core/myta/pages/PageNotFoundPage';
import SelectUserAvatarPage from '@core/avatar/pages/SelectUserAvatarPage';

import { EventProvider } from '@providers/EventProvider';
import ROUTES from '../constants/routes';
import AppWrapper from '@components/AppWrapper';
import EventPageWrapper from '@core/dashboard/components/EventPageWrapper';

const SelectAvatarSkinPage = lazy(
  () => import('@features/avatar/pages/SelectAvatarSkinPage'),
);
const Avaturn = lazy(() => import('@features/avaturn/pages/Avaturn'));
const SelectAvaturnTypePage = lazy(
  () =>
    import(
      '@features/avaturn/pages/SelectAvaturnTypePage/SelectAvaturnTypePage'
    ),
);
const CustomAvaturnProvider = lazy(
  () => import('@features/avaturn/providers/CustomAvaturnProvider'),
);
const CustomAvaturnPage = lazy(
  () => import('@features/avaturn/pages/CustomAvaturnPage'),
);
const ReadyPlayerMe = lazy(
  () => import('@features/readyPlayMe/pages/ReadyPlayerMe'),
);

const APP_ROUTES: RouteObject[] = [
  {
    path: ROUTES.HOME_PAGE,
    element: (
      <AppWrapper moreSpaceAbove>
        <Outlet />
      </AppWrapper>
    ),
    children: [
      {
        index: true,
        element: <LoginPage />,
      },
      {
        path: ROUTES.LOGIN,
        element: <LoginPage />,
      },
    ],
  },
  {
    path: ROUTES.CUSTOM_AVATURN,
    element: (
      <AppWrapper logoHasRedirection={false}>
        <Suspense fallback={<LoadingProgress fullHeight />}>
          <CustomAvaturnProvider>
            <CustomAvaturnPage />
          </CustomAvaturnProvider>
        </Suspense>
      </AppWrapper>
    ),
  },
  {
    path: ROUTES.SELECT_EVENT,
    element: (
      <AppWrapper isAuthorized>
        <SelectEventPage />
      </AppWrapper>
    ),
  },
  {
    path: ROUTES.EXTERNAL_EVENT,
    element: (
      <EventProvider>
        <ExternalEventPage />
      </EventProvider>
    ),
  },
  {
    path: ROUTES.EVENT_PAGE(),
    element: (
      <EventProvider>
        <EventPageContainer />
      </EventProvider>
    ),
    children: [
      {
        path: '',
        element: <EventPageWrapper />,
        children: [
          {
            index: true,
            element: <EventPage />,
          },
          {
            path: ROUTES.SELECT_AVATAR_PROVIDER(),
            element: (
              <AppWrapper isAuthorized>
                <SelectAvatarProviderPage />
              </AppWrapper>
            ),
          },
          {
            path: ROUTES.SELECT_USER_AVATAR(),
            element: (
              <AppWrapper isAuthorized>
                <SelectUserAvatarPage />
              </AppWrapper>
            ),
          },
          {
            path: ROUTES.SELECT_AVATURN(),
            element: (
              <AppWrapper isAuthorized>
                <Suspense fallback={<LoadingProgress fullHeight />}>
                  <SelectAvaturnTypePage />
                </Suspense>
              </AppWrapper>
            ),
          },
          {
            path: ROUTES.CUSTOM_AVATURN_ON_MOBILE(),
            element: (
              <AppWrapper isAuthorized logoHasRedirection={false}>
                <Suspense fallback={<LoadingProgress fullHeight />}>
                  <CustomAvaturnProvider>
                    <CustomAvaturnPage comingFromMobile />
                  </CustomAvaturnProvider>
                </Suspense>
              </AppWrapper>
            ),
          },
          {
            path: ROUTES.SELECT_AVATAR_SKIN(),
            element: (
              <AppWrapper isAuthorized>
                <Suspense fallback={<LoadingProgress fullHeight />}>
                  <SelectAvatarSkinPage />
                </Suspense>
              </AppWrapper>
            ),
          },
          {
            path: ROUTES.READY_PLAYER(),
            element: (
              <AppWrapper isAuthorized>
                <Suspense fallback={<LoadingProgress fullHeight />}>
                  <ReadyPlayerMe />
                </Suspense>
              </AppWrapper>
            ),
          },
          {
            path: ROUTES.AVATURN(),
            element: (
              <Suspense fallback={<LoadingProgress fullHeight />}>
                <Avaturn />
              </Suspense>
            ),
          },
          {
            path: ROUTES.DEVICES_TEST(),
            element: (
              <AppWrapper isAuthorized>
                <DevicesTestPage />
              </AppWrapper>
            ),
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <PageNotFoundPage />,
  },
];

export default APP_ROUTES;
